import React, { useEffect, useMemo, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { ArrowLeft, Check, Mail, Ticket } from 'lucide-react';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import currency from 'helpers/currency';

/**
 * Conteúdo de confirmação do checkout.
 *
 * Substitui o antigo modal de sucesso. Não é um overlay: é renderizado no lugar
 * do conteúdo anterior (o código PIX ou o formulário de cartão), de modo que a
 * mesma superfície muda de estado quando a confirmação chega pelo Pusher —
 * sem salto de contexto nem uma camada nova para o usuário fechar.
 *
 * `variant` diferencia um pagamento já compensado (PIX) de um que ainda está
 * sendo processado pelo adquirente (cartão) — são expectativas diferentes e
 * prometer "confirmado" num cartão em análise gera reclamação.
 */
export default function PaymentSuccess({
    variant = 'confirmed',
    title,
    subtitle,
    amount,
    items,
    onClose,
    onGoToWallet,
}) {
    const screenRef = useRef(null);
    const headingRef = useRef(null);
    const isProcessing = variant === 'processing';

    // O fluxo termina aqui: mover o foco para o título faz o leitor de tela
    // anunciar o desfecho e reposiciona a navegação por teclado no topo.
    //
    // `preventScroll` é obrigatório: sem ele o navegador rola até o título e
    // corta o selo de confirmação acima dele.
    //
    // O checkout rola num container próprio (overflow-y: auto), que ao trocar de
    // conteúdo mantém a posição de onde o usuário estava no formulário. Alinhar
    // este bloco com scrollIntoView não resolve: encostar seu topo na área
    // visível empurra o cabeçalho do checkout para fora. O que reposiciona
    // corretamente é zerar o scroll do container.
    useEffect(() => {
        headingRef.current?.focus({ preventScroll: true });

        let node = screenRef.current?.parentElement;

        while (node) {
            const { overflowY } = window.getComputedStyle(node);

            if (/(auto|scroll)/.test(overflowY) && node.scrollHeight > node.clientHeight) {
                node.scrollTop = 0;
                break;
            }

            node = node.parentElement;
        }

        window.scrollTo(0, 0);
    }, []);

    const orderItems = useMemo(() => {
        if (!Array.isArray(items) || !items.length) {
            return [];
        }

        const groups = new Map();

        items.forEach((item) => {
            const key = item?.id ?? item?.name;
            const group = groups.get(key);

            if (group) {
                group.quantity += 1;
                return;
            }

            groups.set(key, { key, name: item?.name || 'Item', quantity: 1 });
        });

        return [...groups.values()];
    }, [items]);

    const formattedAmount = Number(amount) > 0 ? currency.format(Number(amount)) : null;

    const heading = title || (isProcessing
        ? 'Pagamento em processamento'
        : 'Pagamento confirmado');

    const description = subtitle || (isProcessing
        ? 'Assim que o banco emissor autorizar, seus ingressos ficam disponíveis na carteira.'
        : 'Seus ingressos já estão na sua carteira.');

    return (
        <Screen ref={screenRef} role="region" aria-label="Confirmação da compra">
            <Content>
                {/* Mesmo selo em todos os meios de pagamento: a compra foi concluída
                    com sucesso nos dois casos, e variar o símbolo faria o cartão
                    parecer um desfecho pior que o PIX. O que muda é o texto. */}
                <Badge aria-hidden="true">
                    <Check size={32} color={colors.neutralLight} strokeWidth={3} />
                </Badge>

                <Heading ref={headingRef} tabIndex={-1}>
                    {heading}
                </Heading>

                <Description>{description}</Description>

                {(formattedAmount || !!orderItems.length) && (
                    <ReceiptCard>
                        {formattedAmount && (
                            <ReceiptRow>
                                <ReceiptLabel>Valor pago</ReceiptLabel>
                                <ReceiptAmount>{formattedAmount}</ReceiptAmount>
                            </ReceiptRow>
                        )}

                        {!!orderItems.length && (
                            <ItemList>
                                {orderItems.map(item => (
                                    <ItemRow key={item.key}>
                                        <Ticket size={15} color={colors.night} />
                                        <span>{item.quantity}× {item.name}</span>
                                    </ItemRow>
                                ))}
                            </ItemList>
                        )}
                    </ReceiptCard>
                )}

                <NextSteps>
                    <NextStepsTitle>O que acontece agora</NextStepsTitle>

                    <NextStepRow>
                        <StepIcon><Ticket size={16} color={colors.occasionalPurple} /></StepIcon>
                        <span>
                            {isProcessing
                                ? 'Seus ingressos aparecem na carteira assim que o pagamento for aprovado.'
                                : 'Seus ingressos estão disponíveis em Meus ingressos, prontos para o check-in.'}
                        </span>
                    </NextStepRow>

                    <NextStepRow>
                        <StepIcon><Mail size={16} color={colors.occasionalPurple} /></StepIcon>
                        <span>
                            Enviamos um e-mail com o comprovante e os detalhes do pedido.
                        </span>
                    </NextStepRow>
                </NextSteps>

                <Actions>
                    <PrimaryAction type="button" onClick={onGoToWallet}>
                        <Ticket size={18} color={colors.neutralLight} />
                        Ver meus ingressos
                    </PrimaryAction>

                    <SecondaryAction type="button" onClick={onClose}>
                        <ArrowLeft size={16} color={colors.night} />
                        Voltar ao evento
                    </SecondaryAction>
                </Actions>
            </Content>
        </Screen>
    );
}

const popIn = keyframes`
    0%   { transform: scale(0.6); opacity: 0; }
    60%  { transform: scale(1.08); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
`;

const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const Screen = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    background: ${colors.neutralLight};
    padding: 8px 0 calc(16px + env(safe-area-inset-bottom, 0px));
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 440px;
    margin: auto;
    animation: ${fadeUp} 0.35s ease-out;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

const Badge = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${colors.successDark};
    animation: ${popIn} 0.45s cubic-bezier(0.34, 1.4, 0.64, 1);

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

const Heading = styled.h1`
    margin: 20px 0 0;
    font-size: 24px;
    line-height: 1.3;
    font-weight: 700;
    text-align: center;
    color: ${colors.nightDark};

    &:focus {
        outline: none;
    }

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: 4px;
        border-radius: 4px;
    }

    @media screen and (min-width: ${breakpoints.md}) {
        font-size: 28px;
    }
`;

const Description = styled.p`
    margin: 10px 0 0;
    font-size: 15px;
    line-height: 1.6;
    text-align: center;
    color: ${colors.night};
`;

const ReceiptCard = styled.div`
    width: 100%;
    margin-top: 24px;
    padding: 16px 18px;
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
`;

const ReceiptRow = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
`;

const ReceiptLabel = styled.span`
    font-size: 13px;
    color: ${colors.night};
`;

const ReceiptAmount = styled.strong`
    font-size: 22px;
    font-weight: 700;
    color: ${colors.nightDark};
`;

const ItemList = styled.ul`
    list-style: none;
    margin: 12px 0 0;
    padding: 12px 0 0;
    border-top: 1px solid ${colors.neutralDark};
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ItemRow = styled.li`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: ${colors.night};

    svg {
        flex-shrink: 0;
    }
`;

const NextSteps = styled.section`
    width: 100%;
    margin-top: 20px;
    padding: 16px 18px;
    border-radius: 12px;
    background: #FAF5FF;
`;

const NextStepsTitle = styled.h2`
    margin: 0 0 12px;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: ${colors.occasionalPurple};
`;

const NextStepRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 14px;
    line-height: 1.6;
    color: ${colors.night};

    & + & {
        margin-top: 10px;
    }
`;

const StepIcon = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 7px;
    background: ${colors.neutralLight};
    flex-shrink: 0;
    margin-top: 1px;
`;

const Actions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    margin-top: 28px;
`;

const PrimaryAction = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    min-height: 50px;
    padding: 12px 18px;
    border: none;
    border-radius: 10px;
    background: ${colors.occasionalPurple};
    color: ${colors.neutralLight};
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    transition: opacity 0.15s ease;

    &:hover {
        opacity: 0.92;
    }

    &:focus-visible {
        outline: 2px solid ${colors.nightDark};
        outline-offset: 2px;
    }
`;

const SecondaryAction = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    min-height: 46px;
    padding: 10px 18px;
    border: 1px solid ${colors.neutralDark};
    border-radius: 10px;
    background: transparent;
    color: ${colors.night};
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
        background: ${colors.neutral};
    }

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: 2px;
    }
`;
